import { Component, OnInit, inject, DestroyRef, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router } from '@angular/router';
import { ChartModule } from 'primeng/chart';
import { SkeletonModule } from 'primeng/skeleton';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { forkJoin } from 'rxjs';
import { StatusBadgeComponent } from '@shared/components/status-badge/status-badge.component';
import {
  PostoService,
  EscalaService,
  EfetivoService,
  OcorrenciaService,
  ContratoService,
} from '@core/services/domain-services';
import { CoberturaAlertService } from '@core/services/cobertura-alert.service';
import { Escala, Ocorrencia, Posto } from '@core/models/models';
import { OcorrenciaPorCriticidadeModel, PostoPorNivelRiscoModel, EscalaDeHojeModel, UltimaOcorrenciaModel, } from '@shared/api/services/estatisticas/estatisticas-models';
import { FaturacaoEletronicaService } from '@shared/api/services/faturacao-eletronica.service';

// ESTATISTICAS
import {  PainelGeralEstatisticas } from '@shared/api/services/estatisticas/estatisticas-info.service'; 
@Component({
  selector: 'app-dashboards',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    ChartModule,
    SkeletonModule,
    StatusBadgeComponent,
  ],
  templateUrl: './dashboards.component.html',
  styleUrl: './dashboards.component.scss',
})
export class DashboardsComponent implements OnInit {
  private readonly postoSvc = inject(PostoService);
  private readonly escalaSvc = inject(EscalaService);
  private readonly efetivoSvc = inject(EfetivoService);
  private readonly ocorrenciaSvc = inject(OcorrenciaService);
  private readonly contratoSvc = inject(ContratoService);
  private readonly faturacaoSvc = inject(FaturacaoEletronicaService);
  private readonly coberturaAlertSvc = inject(CoberturaAlertService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);

  // ESTATISTICAS
  private readonly painelGeralInfo = inject(PainelGeralEstatisticas)

  // DADOS PARA O PAINEL GERAL
  postosAtivos = 0; efetivosEmServico = 0; ocorrenciasAbertas = 0;
  equipamentosEmManutencao = 0; contratosAVencer = 0; alertasAtivos = 0;

  ocorrenciasPorCriticidade: OcorrenciaPorCriticidadeModel[] = [];
  postosPorNivelRisco: PostoPorNivelRiscoModel[] = [];
  escalasDeHoje: EscalaDeHojeModel[] = [];
  ultimasOcorrenciasApi: UltimaOcorrenciaModel[] = [];

  loading = false;

  kpis: {
    label: string;
    value: string;
    total?: number;
    percentage?: number;
    type: string;
    icon: string;
    delta?: string;
  }[] = [
    {
      label: 'Efetivos Ativos',
      value: '—',
      type: 'kpi-primary',
      icon: 'ri-user-3-line',
    },
    {
      label: 'Postos Ativos',
      value: '—',
      type: 'kpi-success',
      icon: 'ri-map-pin-line',
    },
    {
      label: 'Ocorrências Urgentes',
      value: '—',
      type: 'kpi-danger',
      icon: 'ri-alert-line',
    },
    {
      label: 'Contratos a Vencer',
      value: '—',
      type: 'kpi-warn',
      icon: 'ri-file-text-line',
    },
  ];

  criticidadeData = {
    labels: ['URGENTE', 'ALTA', 'MÉDIA'],
    datasets: [
      {
        data: [0, 0, 0],
        backgroundColor: [
          'rgba(231,76,60,.7)',
          'rgba(230,126,34,.7)',
          'rgba(241,196,15,.7)',
        ],
        borderRadius: 4,
      },
    ],
  };

  criticidadeOptions = {
    indexAxis: 'y',
    plugins: { legend: { display: false } },
    scales: {
      x: { ticks: { color: '#7d8590' }, grid: { color: 'rgba(42,52,69,.5)' } },
      y: { ticks: { color: '#7d8590' }, grid: { display: false } },
    },
  };

  riscoData = {
    labels: ['CRÍTICO', 'ALTO', 'MODERADO', 'BAIXO'],
    datasets: [
      {
        data: [0, 0, 0, 0],
        backgroundColor: [
          'rgba(192,57,43,.8)',
          'rgba(230,126,34,.8)',
          'rgba(52,152,219,.8)',
          'rgba(39,174,96,.8)',
        ],
        borderWidth: 0,
      },
    ],
  };

  riscoOptions = {
    plugins: {
      legend: {
        position: 'bottom' as const,
        labels: { color: '#7d8590', padding: 16, boxWidth: 12 },
      },
    },
  };

  escalaHoje: Escala[] = [];
  ultimasOcorrencias: Ocorrencia[] = [];

  postosSemCobertura: Posto[] = [];
  loadingCobertura = false;

  equipManutencaoCount: number = 0;
  armasCount: number = 0;
  faturasCount: number = 0;
  viaturasCount: number = 0;

  ngOnInit(): void {
    this.loadPostosSemCobertura();
    this.loadKpis();
    this.estatisticasPainelGeral();
    
  }

  private atualizarGraficoCriticidade(
    dados: OcorrenciaPorCriticidadeModel[]): void {
    this.criticidadeData = {
      labels: dados.map((item) => item.criticidade),
      datasets: [
        {
          data: dados.map((item) => item.total),
          backgroundColor: [
            'rgba(231,76,60,.7)',
            'rgba(230,126,34,.7)',
            'rgba(241,196,15,.7)',
          ],
          borderRadius: 4,
        },
      ],
    };

  }

  private atualizarGraficoRisco(
    dados: PostoPorNivelRiscoModel[]): void {

    this.riscoData = {
      labels: dados.map((item) => item.nivelRisco),
      datasets: [
        {
          data: dados.map((item) => item.total),
          backgroundColor: [
            'rgba(192,57,43,.8)',
            'rgba(230,126,34,.8)',
            'rgba(52,152,219,.8)',
            'rgba(39,174,96,.8)',
          ],
          borderWidth: 0,
        },
      ],
    };

  }

  // PEGA AS INFORMAÇÕES DO PAINEL GERAL
  private estatisticasPainelGeral(): void {
  this.loading = true;

  this.painelGeralInfo
    .obterPainelGeralEstatisticas()
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe({
      next: (estatisticas) => {

        this.postosAtivos = estatisticas.postosAtivos;
        this.efetivosEmServico = estatisticas.efetivosEmServico;
        this.ocorrenciasAbertas = estatisticas.ocorrenciasAbertas;
        this.equipamentosEmManutencao =
          estatisticas.equipamentosEmManutencao;
        this.contratosAVencer = estatisticas.contratosAVencer;
        this.alertasAtivos = estatisticas.alertasAtivos;

        this.ocorrenciasPorCriticidade =
          estatisticas.ocorrenciasPorCriticidade;

        this.postosPorNivelRisco =
          estatisticas.postosPorNivelRisco;

        this.escalasDeHoje =
          estatisticas.escalasDeHoje;

        this.ultimasOcorrenciasApi =
          estatisticas.ultimasOcorrencias;

        this.atualizarGraficoCriticidade(
          estatisticas.ocorrenciasPorCriticidade
        );

        this.atualizarGraficoRisco(
          estatisticas.postosPorNivelRisco
        );

        this.loading = false;
        this.cdr.markForCheck();
      },

      error: (error) => {
        console.error(
          'Erro ao carregar estatísticas do painel geral:',
          error
        );

        this.loading = false;
        this.cdr.markForCheck();
      },
    });
  }

  loadKpis(): void {
    forkJoin({
      ativos: this.efetivoSvc.getAll({ ativo: true } as Record<string, unknown>),
      todos: this.efetivoSvc.getAll({ pageNumber: 1, pageSize: 1 } as Record<string, unknown>)
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ ativos, todos }) => {
        const ativosVal = ativos.totalRecords ?? ativos.data?.length ?? 0;
        const totalVal = todos.totalRecords ?? todos.data?.length ?? 0;
        const perc = totalVal > 0 ? (ativosVal / totalVal) * 100 : 0;
        this.kpis = this.kpis.map((kpi, index) =>
          index === 0 ? { ...kpi, value: `${ativosVal}/${totalVal}`, percentage: perc } : kpi,
        );
        this.cdr.markForCheck();
      });

    forkJoin({
      ativos: this.postoSvc.getAll({ ativo: true } as Record<string, unknown>),
      todos: this.postoSvc.getAll({ pageNumber: 1, pageSize: 1 } as Record<string, unknown>)
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ ativos, todos }) => {
        const ativosVal = ativos.totalRecords ?? ativos.data?.length ?? 0;
        const totalVal = todos.totalRecords ?? todos.data?.length ?? 0;
        const perc = totalVal > 0 ? (ativosVal / totalVal) * 100 : 0;
        this.kpis[1] = { ...this.kpis[1], value: `${ativosVal}/${totalVal}`, percentage: perc };
        this.cdr.markForCheck();
      });

    this.contratoSvc
      .getAll()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((r) => {
        this.kpis[3].value = String(r.totalRecords ?? r.data?.length ?? 0);
        this.cdr.markForCheck();
      });

    this.faturacaoSvc
      .listarDocumentos({ page: 1, pageSize: 1 })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((r) => {
        this.faturasCount = r.totalRecords;
        this.cdr.markForCheck();
      });

    this.ocorrenciaSvc
      .getAll({ tipo: 'EQUIPAMENTO', pageSize: 1000 } as Record<
        string,
        unknown
      >)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((r) => {
        this.equipManutencaoCount = r.totalRecords ?? r.data?.length ?? 0;
        this.cdr.markForCheck();
      });

    this.ocorrenciaSvc
      .getAll({ criticidade: 'URGENTE' } as Record<string, unknown>)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((r) => {
        this.kpis[2].value = String(r.totalRecords ?? r.data?.length ?? 0);
        this.cdr.markForCheck();
      });

    this.ocorrenciaSvc
      .getAll({ tipo: 'ARMA', pageSize: 1000 } as Record<string, unknown>)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((r) => {
        this.armasCount = r.totalRecords ?? r.data?.length ?? 0;
        this.cdr.markForCheck();
      });

    this.ocorrenciaSvc
      .getAll({ tipo: 'VIATURA', pageSize: 1000 } as Record<string, unknown>)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((r) => {
        this.viaturasCount = r.totalRecords ?? r.data?.length ?? 0;
        this.cdr.markForCheck();
      });
  }

  loadPostosSemCobertura(): void {
    this.loadingCobertura = true;
    this.coberturaAlertSvc.postosSemCobertura$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((postos) => {
        this.postosSemCobertura = postos;
        this.loadingCobertura = false;
        this.cdr.markForCheck();
      });
    this.coberturaAlertSvc.refresh();
  }

  irParaEscalasPosto(posto: Posto): void {
    this.router.navigate(['/admin/postos', posto.id]);
  }

  abrirModalOcorrencia(): void {
    this.router.navigate(['/admin/ocorrencias'], {
      queryParams: { novo: 'true' },
    });
  }
  
  exportarDashboard(): void {
    this.router.navigate(['/admin/relatorios']);
  }
}
